import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  Fade,
  FormControlLabel,
  IconButton,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { PopperPlacementType } from "@mui/material/Popper";
import { useEffect, useRef, useState } from "react";
import { FixedSizeList } from "react-window";
import { useDispatch, useSelector } from "react-redux";
import {
  clearOverviewConversionsFilterOptions,
  clearOverviewConversionsFilters,
  removeOverviewConversionsFilter,
  selectBizOverviewConversionsFilterLoading,
  selectBizOverviewConversionsFilterOptions,
  selectBizOverviewConversionsFilters,
  setOverviewConversionsFilter,
} from "../../../../../../features/bizDevelopmentDashboardSlice";
import {
  BizDevelopmentDashboardEndPointLogic,
  BizOverviewConversionsDataSet,
} from "../../../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardEndPointLogic";

interface Props {
  oauthTokenHeader: string;
  dataSet: BizOverviewConversionsDataSet;
  websiteSharedUids: string[];
  dimensions: string[];
  onFiltersChange: (filters: { key: string; value: string }[]) => void;
}

const FILTER_CONFIGS = [
  {
    key: "trafficsource",
    optionKey: "trafficSource",
    label: "Traffic Source",
    displayName: "Select Traffic Source",
    idField: "traffic_source_name",
    nameField: "traffic_source_name",
    searchPlaceholder: "Search traffic source...",
  },
  {
    key: "country",
    optionKey: "country",
    label: "Country",
    displayName: "Select Country",
    idField: "name",
    nameField: "name",
    searchPlaceholder: "Search country...",
  },
  {
    key: "campaignId",
    optionKey: "campaign",
    label: "Campaign",
    displayName: "Select Campaign",
    idField: "camp_id",
    nameField: "camp_name",
    searchPlaceholder: "Search campaign...",
  },
  {
    key: "adgroupId",
    optionKey: "adgroup",
    label: "Adgroup",
    displayName: "Select Adgroup",
    idField: "ag_id",
    nameField: "ag_name",
    searchPlaceholder: "Search adgroup...",
  },
  {
    key: "kwId",
    optionKey: "keyword",
    label: "Keyword",
    displayName: "Select Keyword",
    idField: "kw_id",
    nameField: "kw_name",
    searchPlaceholder: "Search keyword...",
  },
];

export default function OverviewConversionsFilter({
  oauthTokenHeader,
  dataSet,
  websiteSharedUids,
  dimensions,
  onFiltersChange,
}: Props) {
  const dispatch = useDispatch();
  const filters = useSelector(selectBizOverviewConversionsFilters);
  const filterOptions = useSelector(selectBizOverviewConversionsFilterOptions);
  const isFilterLoading = useSelector(
    selectBizOverviewConversionsFilterLoading,
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] =
    useState<PopperPlacementType>("bottom-start");
  const [selectedContentIndex, setSelectedContentIndex] = useState(0);
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const popperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!oauthTokenHeader || dataSet !== "click") return;
    BizDevelopmentDashboardEndPointLogic.getOverviewConversionsBaseFilterOptions(
      oauthTokenHeader,
    );
  }, [dataSet, oauthTokenHeader]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        !anchorEl?.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearchTerms({});
      }
    };

    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [anchorEl, open]);

  const selectedValues = filters.reduce(
    (acc: Record<string, string>, filter: { key: string; value: string }) => {
      acc[filter.key] = filter.value;
      return acc;
    },
    {},
  );
  const selectedTrafficSource = selectedValues.trafficsource;

  const getFilteredData = (config: any) => {
    const rawData = filterOptions?.[config.optionKey];
    const optionRows = Array.isArray(rawData)
      ? rawData
      : Array.isArray(rawData?.query_output)
        ? rawData.query_output
        : [];
    const validData = optionRows.filter(
      (item: any) => item?.[config.idField] && item?.[config.nameField],
    );
    const deduped = Array.from(
      new Map(
        validData.map((item: any) => [item[config.idField], item]),
      ).values(),
    ) as any[];
    const searchTerm = searchTerms[config.key] || "";
    if (!searchTerm) return deduped;
    return deduped.filter((item: any) =>
      item[config.nameField]?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  const getNextFilters = (key: string, value?: string) => {
    const next = filters.filter((filter: any) => filter.key !== key);
    if (value) next.push({ key, value });
    return next;
  };

  const handleOptionChange =
    (filterKey: string) =>
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setOpen(false);
      setSearchTerms({});
      let nextFilters = getNextFilters(filterKey, value);
      if (filterKey === "trafficsource") {
        dispatch(clearOverviewConversionsFilters());
        dispatch(clearOverviewConversionsFilterOptions());
        nextFilters = [{ key: filterKey, value }];
        dispatch(setOverviewConversionsFilter({ key: filterKey, value }));
        onFiltersChange(nextFilters);
        void (async () => {
          await BizDevelopmentDashboardEndPointLogic.getOverviewConversionsBaseFilterOptions(
            oauthTokenHeader,
          );
          await BizDevelopmentDashboardEndPointLogic.getOverviewConversionsDependentFilterOptions(
            {
              oauthTokenHeader,
              websiteSharedUids,
              dimensions,
              trafficSource: value,
            },
          );
        })();
        return;
      }
      dispatch(setOverviewConversionsFilter({ key: filterKey, value }));
      onFiltersChange(nextFilters);
    };

  const handleClearIndividualFilter = (filterKey: string) => {
    let nextFilters = getNextFilters(filterKey);
    if (filterKey === "trafficsource") {
      dispatch(clearOverviewConversionsFilters());
      dispatch(clearOverviewConversionsFilterOptions());
      nextFilters = [];
      BizDevelopmentDashboardEndPointLogic.getOverviewConversionsBaseFilterOptions(
        oauthTokenHeader,
      );
    } else {
      dispatch(removeOverviewConversionsFilter(filterKey));
    }
    onFiltersChange(nextFilters);
  };

  const handleClearFilter = () => {
    dispatch(clearOverviewConversionsFilters());
    onFiltersChange([]);
  };

  const handleClick =
    (newPlacement: PopperPlacementType, index: number) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const config = FILTER_CONFIGS[index];
      const isDisabled =
        config.key !== "trafficsource" &&
        (isFilterLoading || !selectedTrafficSource);
      if (isDisabled) return;
      if (anchorEl === event.currentTarget && open) {
        setOpen(false);
      } else {
        setAnchorEl(event.currentTarget);
        setOpen(true);
        setPlacement(newPlacement);
        setSelectedContentIndex(index);
      }
    };

  const selectedCount = filters.length;

  return (
    <Box sx={{ width: "100%", paddingTop: "8px" }}>
      <Popper
        sx={{ zIndex: 1200 }}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper ref={popperRef}>
              <FilterContent
                config={FILTER_CONFIGS[selectedContentIndex]}
                data={getFilteredData(FILTER_CONFIGS[selectedContentIndex])}
                selectedValue={
                  selectedValues[FILTER_CONFIGS[selectedContentIndex].key] || ""
                }
                searchTerm={
                  searchTerms[FILTER_CONFIGS[selectedContentIndex].key] || ""
                }
                onSearchChange={(value: string) =>
                  setSearchTerms((prev) => ({
                    ...prev,
                    [FILTER_CONFIGS[selectedContentIndex].key]: value,
                  }))
                }
                onOptionChange={handleOptionChange(
                  FILTER_CONFIGS[selectedContentIndex].key,
                )}
              />
            </Paper>
          </Fade>
        )}
      </Popper>

      {FILTER_CONFIGS.map((config, index) => {
        const isSelected = !!selectedValues[config.key];
        const isDisabled =
          config.key !== "trafficsource" &&
          (isFilterLoading || !selectedTrafficSource);

        return (
          <Box
            key={config.key}
            sx={{ display: "inline-flex", alignItems: "center", mr: 1 }}
          >
            <Button
              sx={{
                backgroundColor: isSelected
                  ? "rgba(156, 39, 176, 0.08)"
                  : "transparent",
                textTransform: "capitalize",
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={handleClick("bottom-start", index)}
              disabled={isDisabled}
            >
              <span style={{ color: isSelected ? "#9C27B0" : "#000000DE" }}>
                {config.label}
              </span>
              {isSelected ? (
                <span style={{ color: "#9C27B0" }}>+1</span>
              ) : (
                <KeyboardArrowDownIcon sx={{ color: "#000000DE" }} />
              )}
            </Button>
            {isSelected && (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleClearIndividualFilter(config.key);
                }}
                sx={{ ml: 0.5, color: "#9C27B0", width: 24, height: 24 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        );
      })}

      <Button
        sx={{
          textTransform: "capitalize",
          fontWeight: 500,
          color: "#000000DE",
        }}
        onClick={handleClearFilter}
        disabled={selectedCount === 0}
      >
        Clear Filter {selectedCount > 0 && `(${selectedCount})`}
      </Button>

      {!selectedTrafficSource && (
        <Typography
          sx={{ mt: 1.5, ml: 1, fontSize: "0.8rem", color: "#6b6b6b" }}
        >
          Please select a Traffic Source first — it’s required before applying
          any other filters.
        </Typography>
      )}
    </Box>
  );
}

function FilterContent({
  config,
  data,
  selectedValue,
  searchTerm,
  onSearchChange,
  onOptionChange,
}: any) {
  return (
    <Box
      sx={{
        width: 300,
        minHeight: 150,
        maxHeight: 330,
        overflowY: "auto",
        px: 2,
      }}
    >
      <Box
        sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "#fff", pt: 1 }}
      >
        <TextField
          placeholder={config.searchPlaceholder}
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          sx={{ mb: 2 }}
        />
        <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 2 }}>
          {config.displayName}
        </Typography>
      </Box>
      {data.length > 0 ? (
        <RadioGroup value={selectedValue} onChange={onOptionChange}>
          <FixedSizeList
            height={250}
            width="100%"
            itemSize={54}
            itemCount={data.length}
            itemData={{ items: data, config }}
          >
            {({ index, style, data: listData }) => {
              const item = listData.items[index];
              return (
                <div style={style} key={item[listData.config.idField]}>
                  <FormControlLabel
                    value={String(item[listData.config.idField])}
                    control={<Radio color="secondary" />}
                    label={item[listData.config.nameField]}
                  />
                </div>
              );
            }}
          </FixedSizeList>
        </RadioGroup>
      ) : (
        <Typography sx={{ fontSize: "0.80rem", color: "gray", pb: 2 }}>
          No matching {config.label.toLowerCase()}s found.
        </Typography>
      )}
    </Box>
  );
}
